import { motion } from "framer-motion";

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function EnterpriseSolutions() {
  return (
    <div className="min-h-screen bg-gray-950 :ext-gray-100">

      {/* HERO – SAME AS WEB DEVELOPMENT */}
      <motion.section
        className="relative h-screen flex items-center justify-center text-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80')",
        }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            variants={itemVariants}
          >
            Enterprise <span className="text-orange-500">Solutions</span>
          </motion.h1>

          <motion.p
            className="text-gray-300 text-lg leading-relaxed"
            variants={itemVariants}
          >
            Scalable, secure, and future-ready digital solutions built to power
            large-scale enterprises and complex business ecosystems.
          </motion.p>
        </div>
      </motion.section>

      {/* INTRO */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-3xl font-bold text-orange-600 mb-6">
          Built for Large-Scale Businesses
        </h2>
        <p className="text-lg text-gray-300 leading-relaxed">
          We help enterprises modernize legacy systems, integrate cloud
          platforms, and automate workflows to improve efficiency, security,
          and long-term scalability.
        </p>
      </motion.section>

      {/* CAPABILITIES */}
      <motion.section
        className="py-24 bg-gray-900"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            className="text-3xl font-bold text-center text-orange-600 mb-14"
            variants={itemVariants}
          >
            Core Capabilities
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-10">
            <Capability
              title="Custom Enterprise Software"
              desc="Tailor-made platforms aligned with business workflows."
            />
            <Capability
              title="Cloud & DevOps"
              desc="AWS, Azure & scalable cloud-native systems."
            />
            <Capability
              title="Security & Compliance"
              desc="Enterprise-grade security & regulatory compliance."
            />
          </div>
        </div>
      </motion.section>

      {/* PROCESS */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2
          className="text-3xl font-bold text-orange-600 mb-14 text-center"
          variants={itemVariants}
        >
          Our Enterprise Process
        </motion.h2>

        <div className="grid md:grid-cols-4 gap-8 text-center">
          {[
            ["Discovery", "Business analysis & audit"],
            ["Architecture", "Scalable system design"],
            ["Implementation", "Agile development"],
            ["Optimization", "Monitoring & improvement"],
          ].map(([title, desc], i) => (
            <motion.div
              key={i}
              className="p-8 bg-gray-800 rounded-3xl shadow"
              variants={itemVariants}
            >
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-sm text-gray-300">{desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-28 text-center bg-gradient-to-r from-orange-700 to-orange-900 text-white"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl font-extrabold mb-6">
          Ready to Transform Your Enterprise?
        </h2>
        <p className="text-orange-100 mb-10">
          Let’s build secure, scalable enterprise systems together.
        </p>
        <motion.a
          href="/contact"
          className="inline-block px-12 py-4 bg-black rounded-full text-orange-400 font-semibold"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          Talk to Experts
        </motion.a>
      </motion.section>
    </div>
  );
}

/* Components */

function Capability({ title, desc }) {
  return (
    <motion.div
      className="p-10 rounded-3xl bg-gray-800 shadow"
      variants={itemVariants}
      whileHover={{ y: -8 }}
    >
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-300">{desc}</p>
    </motion.div>
  );
}
